import React, { useEffect, useRef, useState } from 'react';
import AppShell from '@/components/AppShell';
import client, { API, getToken } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/context/AuthContext';
import { UsageMeter } from '@/components/UsageMeter';
import { toast } from 'sonner';
import { Loader2, ShieldCheck, Check, SlidersHorizontal, Building2, Camera, Radar, Gauge } from 'lucide-react';

export default function Settings() {
  const { user } = useAuth();
  const canSystem = user?.role === 'developer';
  const canCompany = ['developer', 'company_admin', 'owner_operator'].includes(user?.role);
  return (
    <AppShell title="Settings">
      <Tabs defaultValue="company">
        <TabsList className="mb-4">
          <TabsTrigger value="company" data-testid="settings-company-tab"><Building2 className="mr-1.5 h-4 w-4" />Company Profile</TabsTrigger>
          <TabsTrigger value="usage" data-testid="settings-usage-tab"><Gauge className="mr-1.5 h-4 w-4" />Usage &amp; Plan</TabsTrigger>
          <TabsTrigger value="preferences" data-testid="settings-preferences-tab"><SlidersHorizontal className="mr-1.5 h-4 w-4" />Preferences</TabsTrigger>
          <TabsTrigger value="roles" data-testid="settings-roles-tab"><ShieldCheck className="mr-1.5 h-4 w-4" />Roles</TabsTrigger>
        </TabsList>
        <TabsContent value="company"><CompanyTab canEdit={canCompany} /></TabsContent>
        <TabsContent value="usage"><UsageTab /></TabsContent>
        <TabsContent value="preferences"><PreferencesTab canEdit={canSystem} /></TabsContent>
        <TabsContent value="roles"><RolesTab /></TabsContent>
      </Tabs>
    </AppShell>
  );
}

function CompanyTab({ canEdit }) {
  const { refreshTenant } = useAuth();
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);
  const [logoKey, setLogoKey] = useState(Date.now());
  const fileRef = useRef();
  useEffect(() => {
    // Legal identity (name/legal/EIN/DOT/MC) comes from the tenant record (per-company);
    // logo/contact/description come from the company profile.
    Promise.all([
      client.get('/company').then((r) => r.data).catch(() => ({})),
      client.get('/tenant/me').then((r) => r.data).catch(() => ({})),
    ]).then(([co, t]) => setC({
      ...co,
      name: t.name ?? co.name ?? '',
      legal_name: t.legal_name ?? co.legal_name ?? '',
      ein: t.ein ?? '',
      dot_number: t.dot_number ?? co.dot_number ?? '',
      mc_number: t.mc_number ?? co.mc_number ?? '',
      tenant_id: t.tenant_id,
      has_logo: !!t.has_logo,
      logo_version: t.logo_version,
    }));
  }, []);
  const upd = (k, v) => setC((p) => ({ ...p, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      // Save legal identity to the tenant, and the rest to the company profile
      await client.put('/tenant/profile', { name: c.name, legal_name: c.legal_name, ein: c.ein, dot_number: c.dot_number, mc_number: c.mc_number });
      const r = await client.put('/company', c);
      setC((p) => ({ ...p, ...r.data, ein: c.ein }));
      await refreshTenant();
      toast.success('Company profile saved');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };
  const uploadLogo = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try {
      // Per-tenant logo: stored on the active workspace so each company has its own brand.
      const r = await client.post('/tenant/logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Logo updated');
      setC((p) => ({ ...p, has_logo: !!r.data.has_logo, logo_version: r.data.logo_version }));
      setLogoKey(Date.now());
      await refreshTenant();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Upload failed'); }
  };
  const removeLogo = async () => {
    try {
      const r = await client.delete('/tenant/logo');
      toast.success('Logo removed');
      setC((p) => ({ ...p, has_logo: false, logo_version: r.data.logo_version }));
      setLogoKey(Date.now());
      await refreshTenant();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  if (!c) return <Skeleton className="h-64" />;
  const logoUrl = c.has_logo && c.tenant_id ? `${API}/tenant/logo/${c.tenant_id}?v=${c.logo_version || logoKey}` : null;
  return (
    <Card className="max-w-3xl p-6">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="font-display text-lg font-semibold">Company Profile</h3>
        {canEdit && <Button onClick={save} disabled={saving} data-testid="company-save-button">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save</Button>}
      </div>
      {!canEdit && <p className="mb-4 rounded-lg bg-muted/50 p-2 text-xs text-muted-foreground">Only Company Owners, Company Admins, or the Developer can edit the company profile.</p>}
      <div className="mb-6 flex items-center gap-4">
        <div className="relative">
          {logoUrl ? <img src={logoUrl} alt="logo" className="h-20 w-20 rounded-xl border object-contain" onError={(e) => { e.target.style.display = 'none'; }} /> : <div className="flex h-20 w-20 items-center justify-center rounded-xl border bg-muted"><Building2 className="h-8 w-8 text-muted-foreground" /></div>}
          {canEdit && <button onClick={() => fileRef.current?.click()} className="absolute -bottom-1 -right-1 flex h-8 w-8 items-center justify-center rounded-full border-2 border-white bg-[hsl(var(--gold))] shadow" data-testid="company-logo-button"><Camera className="h-4 w-4" /></button>}
          <input ref={fileRef} type="file" accept=".png,.jpg,.jpeg,.webp" className="hidden" onChange={(e) => uploadLogo(e.target.files[0])} data-testid="company-logo-input" />
        </div>
        <div className="text-sm text-muted-foreground">
          <p>Upload your company logo (PNG/JPG, max 4MB). It appears in your workspace header and sidebar.</p>
          {canEdit && c.has_logo && (
            <button onClick={removeLogo} className="mt-1.5 text-xs font-medium text-[hsl(var(--danger))] hover:underline" data-testid="company-logo-remove">Remove logo</button>
          )}
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div><Label>Company Name</Label><Input value={c.name || ''} disabled={!canEdit} onChange={(e) => upd('name', e.target.value)} className="mt-1" data-testid="company-name" /></div>
        <div><Label>Legal Name</Label><Input value={c.legal_name || ''} disabled={!canEdit} onChange={(e) => upd('legal_name', e.target.value)} className="mt-1" data-testid="company-legal-name" /></div>
        <div><Label>EIN (Tax ID)</Label><Input value={c.ein || ''} disabled={!canEdit} onChange={(e) => upd('ein', e.target.value)} placeholder="12-3456789" className="mt-1" data-testid="company-ein" /></div>
        <div><Label>DOT Number</Label><Input value={c.dot_number || ''} disabled={!canEdit} onChange={(e) => upd('dot_number', e.target.value)} className="mt-1" data-testid="company-dot" /></div>
        <div><Label>MC Number</Label><Input value={c.mc_number || ''} disabled={!canEdit} onChange={(e) => upd('mc_number', e.target.value)} className="mt-1" data-testid="company-mc" /></div>
        <div><Label>Phone</Label><Input value={c.phone || ''} disabled={!canEdit} onChange={(e) => upd('phone', e.target.value)} className="mt-1" data-testid="company-phone" /></div>
        <div><Label>Email</Label><Input value={c.email || ''} disabled={!canEdit} onChange={(e) => upd('email', e.target.value)} className="mt-1" data-testid="company-email" /></div>
        <div className="sm:col-span-2"><Label>Address</Label><Input value={c.address || ''} disabled={!canEdit} onChange={(e) => upd('address', e.target.value)} className="mt-1" data-testid="company-address" /></div>
        <div className="sm:col-span-2"><Label>Description</Label><textarea value={c.description || ''} disabled={!canEdit} onChange={(e) => upd('description', e.target.value)} rows={3} className="mt-1 w-full rounded-lg border bg-white p-2 text-sm outline-none focus:ring-2 focus:ring-[hsl(var(--ring))] disabled:opacity-60" data-testid="company-description" /></div>
      </div>
    </Card>
  );
}

function PreferencesTab({ canEdit }) {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { client.get('/settings').then((r) => setS(r.data)).catch(() => setS({})); }, []);
  const upd = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const save = async () => {
    setSaving(true);
    try { const r = await client.put('/settings', s); setS(r.data); toast.success('Settings saved'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };
  if (!s) return <Skeleton className="h-64" />;
  return (
    <div className="grid max-w-4xl gap-4 lg:grid-cols-2">
      <Card className="p-6">
        <div className="mb-4 flex items-center justify-between"><h3 className="font-display text-lg font-semibold">System Preferences</h3>{canEdit && <Button onClick={save} disabled={saving} data-testid="settings-save-button">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save</Button>}</div>
        {!canEdit && <p className="mb-4 rounded-lg bg-muted/50 p-2 text-xs text-muted-foreground">Only the Developer can modify system settings.</p>}
        <div className="grid gap-4 sm:grid-cols-2">
          <div><Label>Timezone</Label><Input value={s.timezone || ''} disabled={!canEdit} onChange={(e) => upd('timezone', e.target.value)} className="mt-1" /></div>
          <div><Label>Expiration Warning (days)</Label><Input type="number" value={s.expiration_warning_days ?? 21} disabled={!canEdit} onChange={(e) => upd('expiration_warning_days', Number(e.target.value))} className="mt-1" /></div>
          <div><Label>Renewal Reminder (days before expiry)</Label><Input type="number" value={s.renewal_reminder_days ?? 30} disabled={!canEdit} onChange={(e) => upd('renewal_reminder_days', Number(e.target.value))} className="mt-1" data-testid="settings-renewal-days" /></div>
          <div><Label>Outgoing Sender Email</Label><Input value={s.sender_email || ''} disabled={!canEdit} onChange={(e) => upd('sender_email', e.target.value)} placeholder="no-reply@nuxfleet.com" className="mt-1" data-testid="settings-sender-email" /><p className="mt-1 text-[10px] text-muted-foreground">Must be a verified sender/domain in your email provider or mail won't deliver.</p></div>
        </div>
        <div className="mt-5 space-y-3">
          {[['notify_email', 'Email notifications'], ['notify_in_app', 'In-app notifications'], ['audit_mode_enabled', 'Enable Audit-Ready inspector mode'], ['inbox_digest_enabled', 'Daily inbox email digest']].map(([k, l]) => (
            <div key={k} className="flex items-center gap-2"><Switch checked={!!s[k]} disabled={!canEdit} onCheckedChange={(v) => upd(k, v)} data-testid={`settings-${k}`} /><span className="text-sm">{l}</span></div>
          ))}
        </div>
      </Card>
      <Card className="p-6">
        <div className="mb-3 flex items-center gap-2 font-display text-lg font-semibold"><Radar className="h-5 w-5 text-[hsl(var(--primary))]" />Automated Daily Scan</div>
        <p className="mb-4 text-sm text-muted-foreground">When enabled, the system scans all documents every day and automatically emails expiration warnings — no manual "Scan" click needed.</p>
        <div className="flex items-center gap-2"><Switch checked={s.auto_scan_enabled !== false} disabled={!canEdit} onCheckedChange={(v) => upd('auto_scan_enabled', v)} data-testid="settings-auto_scan_enabled" /><span className="text-sm">Daily auto-scan {s.auto_scan_enabled !== false ? 'enabled' : 'disabled'}</span></div>
        <div className="mt-4 flex items-center gap-3">
          <Label className="shrink-0 text-sm">Digest send hour (UTC)</Label>
          <Select value={String(s.inbox_digest_hour ?? 6)} onValueChange={(v) => upd('inbox_digest_hour', Number(v))} disabled={!canEdit}>
            <SelectTrigger className="w-28" data-testid="settings-digest-hour"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-60">{Array.from({ length: 24 }).map((_, h) => <SelectItem key={h} value={String(h)}>{String(h).padStart(2, '0')}:00</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="mt-4 rounded-lg border bg-muted/30 p-3 text-xs text-muted-foreground">
          <div>Runs daily at 07:00 UTC.</div>
          <div className="mt-1">Last run: <span className="font-medium text-foreground">{s.last_auto_scan_at ? new Date(s.last_auto_scan_at).toLocaleString() : 'Not run yet'}</span></div>
          {s.last_auto_scan_result && <div className="mt-0.5">Last result: {s.last_auto_scan_result.created} notifications · {s.last_auto_scan_result.emailed} emails</div>}
        </div>
      </Card>
      {canEdit && <FmcsaControl />}
    </div>
  );
}

function FmcsaControl() {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { client.get('/fmcsa').then((r) => setF(r.data)).catch(() => setF({})); }, []);
  const save = async () => {
    setSaving(true);
    try { const r = await client.put('/fmcsa', { status: f.status, iss_score: Number(f.iss_score), dot_number: f.dot_number }); setF((p) => ({ ...p, ...r.data })); toast.success('FMCSA status updated'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };
  if (!f) return null;
  return (
    <Card className="p-6 lg:col-span-2">
      <div className="mb-4 flex items-center justify-between"><h3 className="font-display text-lg font-semibold">FMCSA Credentials</h3><Button onClick={save} disabled={saving} data-testid="fmcsa-settings-save">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save</Button></div>
      <p className="mb-4 text-xs text-muted-foreground">Manually set verification status until the live FMCSA Portal API is connected.</p>
      <div className="grid gap-4 sm:grid-cols-3">
        <div><Label>Status</Label><Select value={f.status} onValueChange={(v) => setF((p) => ({ ...p, status: v }))}><SelectTrigger className="mt-1" data-testid="fmcsa-status-select"><SelectValue /></SelectTrigger><SelectContent>{['Active', 'Invalid', 'Syncing'].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent></Select></div>
        <div><Label>ISS Score</Label><Input type="number" value={f.iss_score ?? 97} onChange={(e) => setF((p) => ({ ...p, iss_score: e.target.value }))} className="mt-1" /></div>
        <div><Label>DOT Number</Label><Input value={f.dot_number || ''} onChange={(e) => setF((p) => ({ ...p, dot_number: e.target.value }))} className="mt-1" /></div>
      </div>
    </Card>
  );
}

function UsageTab() {
  return (
    <div className="space-y-4">
      <UsageMeter variant="detailed" />
      <p className="max-w-2xl text-xs text-muted-foreground">
        Counts reflect active drivers and power units in your workspace. Basic plans are capped at 10 of each; Corporate is unlimited.
      </p>
    </div>
  );
}

function RolesTab() {
  const [roles, setRoles] = useState([]);
  useEffect(() => { client.get('/roles').then((r) => setRoles(r.data)).catch(() => {}); }, []);
  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      {roles.map((r) => (
        <Card key={r.role} className="p-5"><div className="mb-2 flex items-center gap-2 font-display font-semibold"><ShieldCheck className="h-4 w-4 text-[hsl(var(--primary))]" />{r.label}</div>
          <ul className="space-y-1">{r.permissions.map((p) => <li key={p} className="flex items-center gap-1.5 text-sm text-muted-foreground"><Check className="h-3.5 w-3.5 text-[hsl(var(--success))]" />{p.replace(/_/g, ' ')}</li>)}</ul>
        </Card>
      ))}
    </div>
  );
}
